import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Copy } from "lucide-react";
import { MODE, CURRENCIES, fmtMoney, equivalent } from "@/lib/format";
import { planLocked, withPlan, balanceCents, toCents } from "@/lib/validate";
import { FlightsSection } from "@/components/FlightsSection";

const CurrencyPicker = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="radiogroup" aria-label="Moneda">
    {CURRENCIES.map((c) => (
      <button key={c} type="button" role="radio" aria-checked={value === c} onClick={() => onChange(c)} data-testid={`currency-${c}`}
        className={`rounded-md px-5 py-1.5 text-sm font-semibold transition-colors duration-200 ${value === c ? "bg-white text-[#2F5BFF] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{c}</button>
    ))}
  </div>
);

const DELIVERY = [
  ["auto", Mail, "Envío automático", "Se envía al correo del huésped al generar el enlace"],
  ["manual", Copy, "Envío manual", "Tú copias el enlace y lo compartes"],
];

const DeliveryPicker = ({ value, onChange }) => (
  <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2 lg:col-span-3" role="radiogroup" aria-label="Envío del enlace">
    {DELIVERY.map(([v, Icon, title, desc]) => (
      <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`delivery-mode-${v}`}
        className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors duration-200 ${value === v ? "border-[#2F5BFF] bg-[#2F5BFF]/5" : "border-slate-200 hover:border-slate-300"}`}>
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${value === v ? "bg-[#2F5BFF] text-white" : "bg-slate-100 text-slate-500"}`}><Icon size={17} /></span>
        <span><span className="block text-sm font-semibold text-slate-900">{title}</span><span className="block text-xs text-slate-500">{desc}</span></span>
      </button>
    ))}
  </div>
);

const Field = ({ id, label, error, hint, children, className = "", req, opt }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}{req && <span className="ml-0.5 text-rose-500" data-testid={`required-${id}`}>*</span>}
      {opt && <span className="ml-1 font-normal normal-case tracking-normal text-slate-400" data-testid={`optional-${id}`}>(opcional)</span>}
    </Label>
    {children}
    {error ? <p className="text-xs font-medium text-rose-600" data-testid={`error-${id}`}>{error}</p>
      : hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
  </div>
);

const Section = ({ title, children }) => (
  <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
    <legend className="px-2 font-heading text-base font-semibold text-slate-900">{title}</legend>
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </fieldset>
);

const GroupField = ({ groups, form, setForm, error }) => (
  <Field id="group_id" label="Grupo del cobro" req error={error} hint="Estás en varios grupos: elige a cuál pertenece">
    <Select value={form.group_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, group_id: v }))}>
      <SelectTrigger id="group_id" data-testid="select-charge-group" className={error ? "border-rose-400" : ""}><SelectValue placeholder="Selecciona un grupo" /></SelectTrigger>
      <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id} data-testid={`charge-group-option-${g.id}`}>{g.name}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

const NONE = "__none__";
const OTHER = "__other__";
const TRANSPORTS = ["Apto - Hotel - Apto (Redondo)", "Apto - Hotel - Apto (Privado)"];

const CountSelect = ({ id, testid, min, max, optional, value, onChange, error }) => (
  <Select value={value === "" || value == null ? (optional ? NONE : undefined) : String(value)} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
    <SelectTrigger id={id} data-testid={testid} className={error ? "border-rose-400" : ""}><SelectValue placeholder="Selecciona" /></SelectTrigger>
    <SelectContent className="max-h-64">
      {optional && <SelectItem value={NONE}>Sin especificar</SelectItem>}
      {Array.from({ length: max - min + 1 }, (_, i) => String(min + i)).map((n) => <SelectItem key={n} value={n} data-testid={`${testid}-option-${n}`}>{n}</SelectItem>)}
    </SelectContent>
  </Select>
);

const OptionsField = ({ id, options, value, onChange, noneLabel, prefix, otherPlaceholder }) => {
  const [other, setOther] = useState(!!value && !options.includes(value));
  const sel = other ? OTHER : options.includes(value) ? value : NONE;
  const pick = (v) => { setOther(v === OTHER); onChange(v === OTHER || v === NONE ? "" : v); };
  return (
    <div className="space-y-2">
      <Select value={sel} onValueChange={pick}>
        <SelectTrigger id={id} data-testid={`select-${prefix}`}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={NONE} data-testid={`${prefix}-option-none`}>{noneLabel}</SelectItem>
          {options.map((t, i) => <SelectItem key={t} value={t} data-testid={`${prefix}-option-${i}`}>{t}</SelectItem>)}
          <SelectItem value={OTHER} data-testid={`${prefix}-option-other`}>Otra</SelectItem>
        </SelectContent>
      </Select>
      {other && <Input data-testid={`input-${prefix}-other`} value={value} onChange={(e) => onChange(e.target.value)} placeholder={otherPlaceholder} autoFocus />}
    </div>
  );
};

const TransportField = ({ value, onChange }) => (
  <OptionsField id="transportation" options={TRANSPORTS} value={value} onChange={onChange} noneLabel="Sin transportación" prefix="transportation" otherPlaceholder="Describe la transportación" />
);

const PropertyField = ({ properties, form, setForm, error }) => (
  <Field id="property_name" label="Hotel" req error={error} hint="Elige primero el hotel y luego la habitación">
    <Select value={form.property_name || undefined} onValueChange={(v) => setForm((f) => ({ ...f, property_name: v, room: f.property_name === v ? f.room : "" }))}>
      <SelectTrigger id="property_name" data-testid="select-property" className={error ? "border-rose-400" : ""}><SelectValue placeholder="Selecciona el hotel" /></SelectTrigger>
      <SelectContent>{properties.map((p, i) => <SelectItem key={p} value={p} data-testid={`property-option-${i}`}>{p}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

export const ChargeForm = ({ form, setForm, errors, processors, defaultLang = "es", allowManual = false, groups = [], properties = [], propertyRooms = {} }) => {
  const rooms = propertyRooms[form.property_name] || [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPlan = (k) => (e) => setForm((f) => withPlan({ ...f, [k]: e.target.value }));
  const locked = planLocked(form);
  const balance = balanceCents(form);
  const equiv = equivalent(toCents(form.amount), form.currency, form.exchange_rate);
  const inp = (k, testid, props = {}) => (
    <Input id={k} data-testid={testid} value={form[k]} onChange={set(k)} aria-invalid={!!errors[k]}
      className={errors[k] ? "border-rose-400 focus-visible:ring-rose-300" : ""} {...props} />
  );
  const count = (k, testid, min, max, optional) => (
    <CountSelect id={k} testid={testid} min={min} max={max} optional={optional} value={form[k]} error={errors[k]}
      onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} />
  );
  const enabled = processors.filter((p) => p.enabled && p.currency === form.currency);
  const setCurrency = (cur) => setForm((f) => ({ ...f, currency: cur, processor_id: f.processor_id === "manual" || processors.find((p) => p.id === f.processor_id)?.currency === cur ? f.processor_id : "" }));

  return (
    <div className="space-y-6" data-testid="charge-form">
      <p className="text-xs text-slate-500" data-testid="required-legend"><span className="text-rose-500">*</span> Obligatorio para generar el enlace. Puedes guardar el borrador sin llenarlos.</p>
      <Section title="Huésped">
        <Field id="guest_name" label="Nombre del huésped" req error={errors.guest_name}>{inp("guest_name", "input-guest-name", { placeholder: "Nombre completo" })}</Field>
        <Field id="email" label="Correo electrónico" req error={errors.email}>{inp("email", "input-guest-email", { type: "email", placeholder: "correo@ejemplo.com" })}</Field>
        <Field id="phone" label="Teléfono" opt error={errors.phone}>{inp("phone", "input-guest-phone", { type: "tel" })}</Field>
      </Section>
      <Section title="Estancia">
        {properties.length > 0 && <PropertyField properties={properties} form={form} setForm={setForm} error={errors.property_name} />}
        <Field id="room" label="Habitación" opt error={errors.room} hint={rooms.length ? null : "Texto libre"}>
          {rooms.length
            ? <OptionsField key={form.property_name} id="room" options={rooms} value={form.room} onChange={(v) => setForm((f) => ({ ...f, room: v }))} noneLabel="Sin especificar" prefix="room" otherPlaceholder="Escribe la habitación" />
            : inp("room", "input-room-number")}
        </Field>
        <Field id="check_in" label="Check-In" opt error={errors.check_in}>{inp("check_in", "input-check-in", { type: "date" })}</Field>
        <Field id="check_out" label="Check-Out" opt error={errors.check_out}>{inp("check_out", "input-check-out", { type: "date", min: form.check_in || undefined })}</Field>
        <Field id="adults" label="Adultos" req error={errors.adults}>{count("adults", "select-adults-count", 1, 20)}</Field>
        <Field id="minors" label="Menores" req error={errors.minors}>{count("minors", "select-minors-count", 0, 20)}</Field>
        <Field id="infants" label="Infantes" opt error={errors.infants}>{count("infants", "select-infants-count", 0, 10, true)}</Field>
      </Section>
      <Section title="Extras">
        <Field id="resort_credits" label="Resort Credits (USD)" opt error={errors.resort_credits} hint="Informativo: no se descuenta del importe">{inp("resort_credits", "input-resort-credits", { placeholder: "Ej. 500" })}</Field>
        <Field id="transportation" label="Transportación" opt error={errors.transportation}>
          <TransportField value={form.transportation} onChange={(v) => setForm((f) => ({ ...f, transportation: v }))} />
        </Field>
        <Field id="additional_services" label="Servicios adicionales" opt error={errors.additional_services} hint="Texto libre" className="sm:col-span-2 lg:col-span-1">
          <Textarea id="additional_services" data-testid="input-additional-services" rows={2} value={form.additional_services} onChange={set("additional_services")} />
        </Field>
      </Section>
      <FlightsSection value={form.flights} errors={errors} onChange={(v) => setForm((f) => ({ ...f, flights: v }))} />
      <Section title="Cobro">
        <Field id="currency" label="Moneda" hint="Define qué procesadores puedes usar">
          <div><CurrencyPicker value={form.currency} onChange={setCurrency} /></div>
        </Field>
        <Field id="total_amount" label={`Monto total de reservación (${form.currency})`} opt error={errors.total_amount}>
          {inp("total_amount", "input-total-amount", { inputMode: "decimal", placeholder: "0.00", className: `font-mono ${errors.total_amount ? "border-rose-400" : ""}`, onChange: setPlan("total_amount") })}
        </Field>
        <Field id="initial_payment" label={`Pago inicial (${form.currency})`} opt error={errors.initial_payment} hint="Si es menor que el total, es lo que se cobra en el enlace">
          {inp("initial_payment", "input-initial-payment", { inputMode: "decimal", placeholder: "0.00", className: `font-mono ${errors.initial_payment ? "border-rose-400" : ""}`, onChange: setPlan("initial_payment") })}
        </Field>
        <Field id="balance" label="Saldo pendiente" hint="Se calcula automáticamente: total − monto a cobrar">
          <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-sm font-semibold text-slate-700" data-testid="balance-display">
            {balance == null ? "—" : fmtMoney(balance, form.currency)}
          </div>
        </Field>
        <Field id="amount" label={`Monto a cobrar (${form.currency})`} req error={errors.amount} hint={locked ? "Se toma del Pago inicial" : null}>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400" data-testid="amount-currency-prefix">{form.currency}</span>
            {inp("amount", "input-amount-usd", { inputMode: "decimal", placeholder: "0.00", disabled: locked, className: `pl-12 font-mono ${errors.amount ? "border-rose-400" : ""}`, onBlur: () => { const v = Number(form.amount); if (form.amount && /^\d+(\.\d{0,2})?$/.test(form.amount) && !isNaN(v)) setForm((f) => ({ ...f, amount: v.toFixed(2) })); } })}
          </div>
        </Field>
        <Field id="exchange_rate" label="Tipo de cambio (MXN por 1 USD)" opt error={errors.exchange_rate} hint={equiv ? `Equivalente del cobro: ${equiv}` : "Para mostrar el equivalente en la otra moneda"}>
          {inp("exchange_rate", "input-exchange-rate", { inputMode: "decimal", placeholder: "Ej. 17.50", className: `font-mono ${errors.exchange_rate ? "border-rose-400" : ""}` })}
        </Field>
        <Field id="processor_id" label="Procesador de pago" req error={errors.processor_id} hint={enabled.length ? null : `No hay procesadores habilitados en ${form.currency}. Regístralos en Procesadores de pago.`}>
          <Select value={form.processor_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, processor_id: v }))}>
            <SelectTrigger id="processor_id" data-testid="select-payment-processor" className={errors.processor_id ? "border-rose-400" : ""}><SelectValue placeholder="Selecciona un procesador" /></SelectTrigger>
            <SelectContent>
              {enabled.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`processor-option-${p.id}`}>
                  {p.name} · {MODE[p.mode]}{p.connected ? "" : " · pendiente de conexión"}
                </SelectItem>
              ))}
              {(allowManual || form.processor_id === "manual") && <SelectItem value="manual" data-testid="processor-option-manual">Enlace de pago manual</SelectItem>}
            </SelectContent>
          </Select>
        </Field>
        {form.processor_id === "manual" && (
          <Field id="manual_url" label="Enlace de pago" req error={errors.manual_url} hint="El cliente verá tu plantilla y el botón de pago abrirá este enlace" className="sm:col-span-2">
            {inp("manual_url", "input-manual-url", { type: "url", placeholder: "https://..." })}
          </Field>
        )}
        <Field id="language" label="Idioma de la página de pago" hint="El cliente verá la página y el correo en este idioma">
          <Select value={form.language || defaultLang} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
            <SelectTrigger id="language" data-testid="select-pay-language"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="es" data-testid="pay-language-es">Español</SelectItem>
              <SelectItem value="en" data-testid="pay-language-en">English</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field id="expires_hours" label="Caducidad del enlace">
          <Select value={form.expires_hours} onValueChange={(v) => setForm((f) => ({ ...f, expires_hours: v }))}>
            <SelectTrigger id="expires_hours" data-testid="select-expires-hours"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["24", "24 horas"], ["48", "48 horas"], ["72", "72 horas"], ["168", "7 días"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        {groups.length > 1 && <GroupField groups={groups} form={form} setForm={setForm} error={errors.group_id} />}
        <DeliveryPicker value={form.delivery_mode} onChange={(v) => setForm((f) => ({ ...f, delivery_mode: v }))} />
      </Section>
    </div>
  );
};
